/**
 * AircraftLayer Component (2D Leaflet Map)
 *
 * Displays live aircraft positions on the 2D map.
 *
 * Data source priority:
 * 1. CloudFront cache (VITE_AIRCRAFT_CACHE_URL) — 5-second poll with dead reckoning
 * 2. Legacy OpenSky API via backend — 30-second poll (fallback)
 *
 * When using the cache, aircraft positions are smoothly interpolated at 60fps
 * using dead reckoning (velocity + heading prediction).
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Marker, Popup, Polyline } from 'react-leaflet';
import { AircraftState } from '../types/events';
import { fetchAircraft, fetchAircraftCached, isAircraftCacheAvailable } from '../api/client';
import { AircraftTracker, InterpolatedAircraft } from '../utils/aircraftInterpolation';
import { isLowEndDevice } from '../utils/device';
import AircraftDetailCard from './AircraftDetailCard';

interface AircraftLayerProps {
  enabled: boolean;
  helicoptersOnly?: boolean;
  /** Called when the user clicks "Ride Along" in an aircraft popup.
   *  The 2D map can't render a cockpit view itself, so we delegate up to
   *  App.tsx, which switches the view mode to 3D and seeds Map3D with
   *  the icao24 to follow in cockpit mode. */
  onRideAlong?: (icao24: string) => void;
}

interface AircraftWithHistory extends AircraftState {
  history: Array<{ lat: number; lon: number; timestamp: number }>;
}

/** Poll intervals */
const CACHE_POLL_INTERVAL_MS = 5_000;    // CloudFront cache — 5 seconds
const LEGACY_POLL_INTERVAL_MS = 30_000;  // Legacy OpenSky — 30 seconds
// Interpolation tick rate. 10 Hz on capable devices, 5 Hz on low-end —
// every tick triggers a full React reconcile of up to 250 aircraft markers
// + trails, which on a Snapdragon 6 was the dominant CPU cost (P0-1 in
// the 2D perf audit). 5 Hz drops that cost by half with no perceptible
// motion difference at typical aircraft speeds (visual lag ~200ms).
const INTERPOLATION_INTERVAL_FAST_MS = 100;
const INTERPOLATION_INTERVAL_SLOW_MS = 200;

/** Trail settings */
const MAX_HISTORY_POINTS = 60;

export default function AircraftLayer({ enabled, helicoptersOnly = false, onRideAlong }: AircraftLayerProps) {
  const map = useMap();
  const [aircraft, setAircraft] = useState<(InterpolatedAircraft | AircraftWithHistory)[]>([]);

  // Refs for tracking state
  const trackerRef = useRef<AircraftTracker | null>(null);
  const aircraftHistoryRef = useRef<Map<string, Array<{ lat: number; lon: number; timestamp: number }>>>(new Map());
  const useCacheRef = useRef(isAircraftCacheAvailable());

  // L.DivIcon cache keyed by (variant, headingBucket). 250 markers × 10 Hz
  // previously built 2500 fresh divIcons + SVG template literals per second.
  // Heading drifts <1° per tick at typical airliner speeds, so the bucket
  // is stable across many ticks — we typically reuse the same icon for
  // dozens of consecutive frames. Heading rounded to 5° → 72 buckets,
  // × 8 variant combos (helicopter / emergency / on-ground bools) = ~576
  // icons total. Map is cleared on unmount.
  const iconCacheRef = useRef<Map<string, L.DivIcon>>(new Map());

  // Trail positions keyed by icao24. Rebuilt only on server poll (every 5s),
  // not on every interpolation tick — trail TAIL only grows when new
  // samples arrive, and the trail HEAD lag is invisible because the marker
  // itself shows the current interpolated position. Previously
  // getTrailPositions(ac) ran per-aircraft per-tick = ~15k [lat,lon]
  // allocations/sec at peak. Now ~250 allocations per 5s poll = 50/sec.
  // Stored as a stable array reference per icao24 so react-leaflet's
  // Polyline diff sees the same prop ref between polls and skips redraw.
  const trailsByIcaoRef = useRef<Map<string, [number, number][]>>(new Map());

  // L.Marker refs keyed by icao24. Populated by the Marker's ref callback;
  // entries are deleted when the Marker unmounts (ref called with null).
  // The interpolation tick calls marker.setLatLng() + marker.setIcon()
  // imperatively against these refs — bypassing React entirely between
  // discrete-signature changes. That drops reconciles from ~10/sec
  // (one per interpolation tick) to ~1/sec (one per server poll, only
  // when the set of aircraft or a boolean attribute changed).
  const markerRefsByIcaoRef = useRef<Map<string, L.Marker>>(new Map());

  // Discrete signature of the current aircraft set: sorted-comma-joined
  // ${icao24}|${emergency}|${onGround}|${helicopter}. When THIS changes
  // (new aircraft, dropped aircraft, or a boolean attr flipped), we
  // setAircraft to trigger React reconcile and remount markers.
  // Otherwise the imperative loop handles position+icon updates only.
  const lastDiscreteSignatureRef = useRef<string>('');

  // Viewport bounds for filtering off-screen aircraft. Mirrors the pattern
  // in HotspotLayer / PowerOutageLayer. 150ms debounce so flick-pan inertia
  // (which emits several moveend events) doesn't refilter on every tick.
  // Stored as primitives so the useMemo at filter site can compare by value.
  const [viewBounds, setViewBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);

  useEffect(() => {
    if (!map) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const update = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const b = map.getBounds();
        setViewBounds({
          north: b.getNorth(),
          south: b.getSouth(),
          east: b.getEast(),
          west: b.getWest(),
        });
      }, 150);
    };
    update();
    map.on('moveend', update);
    map.on('zoomend', update);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map]);

  // Initialize tracker
  useEffect(() => {
    if (useCacheRef.current) {
      trackerRef.current = new AircraftTracker();
    }
    const iconCache = iconCacheRef.current;
    const markerRefs = markerRefsByIcaoRef.current;
    const trailRefs = trailsByIcaoRef.current;
    return () => {
      trackerRef.current?.clear();
      // Release cached DivIcons, marker refs, and trail data so they don't
      // outlive the component.
      iconCache.clear();
      markerRefs.clear();
      trailRefs.clear();
      lastDiscreteSignatureRef.current = '';
    };
  }, []);

  // ============================================================================
  // Cache-based polling (primary path)
  // ============================================================================

  const fetchCachedData = useCallback(async () => {
    try {
      const response = await fetchAircraftCached();
      if (!response) return;

      const tracker = trackerRef.current;
      if (!tracker) return;

      // Filter for helicopters if requested
      let filteredAircraft = response.aircraft;
      if (helicoptersOnly) {
        filteredAircraft = filteredAircraft.filter(ac => ac.is_helicopter);
      }

      tracker.updateFromServer(filteredAircraft);

      // Rebuild trail data from the tracker's position history. Runs once
      // per 5s poll instead of per 100ms interpolation tick.
      const newTrails = new Map<string, [number, number][]>();
      for (const ac of tracker.getInterpolatedPositions()) {
        const history = tracker.getPositionHistory(ac.icao24);
        if (history.length >= 2) {
          newTrails.set(
            ac.icao24,
            history.map((h) => [h.lat, h.lon] as [number, number])
          );
        }
      }
      trailsByIcaoRef.current = newTrails;
    } catch (err) {
      console.error('[AircraftLayer] Cache fetch error:', err);
    }
  }, [helicoptersOnly]);

  // ============================================================================
  // Legacy polling (fallback path)
  // ============================================================================

  const fetchLegacyData = useCallback(async () => {
    try {
      const bounds = map.getBounds();
      const response = await fetchAircraft({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
        helicoptersOnly,
      });

      const now = Date.now();
      const updatedAircraft: AircraftWithHistory[] = response.aircraft.map((ac) => {
        const existingHistory = aircraftHistoryRef.current.get(ac.icao24) || [];

        if (ac.latitude !== null && ac.longitude !== null) {
          existingHistory.push({
            lat: ac.latitude,
            lon: ac.longitude,
            timestamp: now,
          });
          while (existingHistory.length > MAX_HISTORY_POINTS) {
            existingHistory.shift();
          }
        }

        aircraftHistoryRef.current.set(ac.icao24, existingHistory);
        return { ...ac, history: [...existingHistory] };
      });

      setAircraft(updatedAircraft);

      // Rebuild trail data once per poll (matches the cache-path treatment).
      const newTrails = new Map<string, [number, number][]>();
      for (const ac of updatedAircraft) {
        if (ac.history && ac.history.length >= 2) {
          newTrails.set(
            ac.icao24,
            ac.history.map((h) => [h.lat, h.lon] as [number, number])
          );
        }
      }
      trailsByIcaoRef.current = newTrails;
    } catch (err) {
      console.error('[AircraftLayer] Legacy fetch error:', err);
    }
  }, [map, helicoptersOnly]);

  // ============================================================================
  // Polling setup
  // ============================================================================

  useEffect(() => {
    if (!enabled) {
      setAircraft([]);
      return;
    }

    const useCache = useCacheRef.current;

    if (useCache) {
      // Cache mode: poll CloudFront every 5 seconds
      fetchCachedData();
      const pollInterval = setInterval(fetchCachedData, CACHE_POLL_INTERVAL_MS);

      // Interpolation loop. Tick rate adapts to device capability (10 Hz
      // on capable devices, 5 Hz on low-end — P0-1 in the 2D perf audit).
      // Body bails out when the tab is hidden so a backgrounded TWA stops
      // burning CPU on aircraft updates the user can't see (P0-4 mirror
      // of the 3D fix).
      const intervalMs = isLowEndDevice()
        ? INTERPOLATION_INTERVAL_SLOW_MS
        : INTERPOLATION_INTERVAL_FAST_MS;
      const interpInterval = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        const tracker = trackerRef.current;
        if (!tracker) return;
        const interpolated = tracker.getInterpolatedPositions();

        // Discrete-signature check — only re-render React (and remount
        // Markers) when the SET of aircraft changes or a boolean attribute
        // flips. Everything else (position + heading) goes through the
        // imperative path below. Sorted so insertion order doesn't matter.
        let signature = '';
        for (const ac of interpolated) {
          signature += `${ac.icao24}|${ac.is_emergency ? 1 : 0}|${ac.on_ground ? 1 : 0}|${ac.is_helicopter ? 1 : 0},`;
        }
        if (signature !== lastDiscreteSignatureRef.current) {
          lastDiscreteSignatureRef.current = signature;
          setAircraft(interpolated);
          // Skip imperative update this tick — React will mount/swap the
          // markers on commit, and the JSX `position` prop carries the
          // current interpLat/interpLon so they're correct from frame 0.
          return;
        }

        // Imperative position + icon + opacity update for existing markers.
        // No React reconcile, no JSX evaluation. Marker.setLatLng updates
        // the DOM directly; setIcon is gated on the cached icon identity
        // changing (heading bucket flipped to a new 5° bin); setOpacity
        // handles stale-data fade.
        const markers = markerRefsByIcaoRef.current;
        for (const ac of interpolated) {
          const marker = markers.get(ac.icao24);
          if (!marker) continue;
          marker.setLatLng([ac.interpLat, ac.interpLon]);
          const newIcon = getAircraftIcon(
            ac.interpHeading,
            ac.is_helicopter,
            ac.on_ground,
            ac.is_emergency
          );
          if (marker.options.icon !== newIcon) {
            marker.setIcon(newIcon);
          }
          marker.setOpacity(ac.isStale ? 0.4 : 1);
        }
      }, intervalMs);

      return () => {
        clearInterval(pollInterval);
        clearInterval(interpInterval);
      };
    } else {
      // Legacy mode: poll via API Gateway every 30 seconds
      fetchLegacyData();
      const pollInterval = setInterval(fetchLegacyData, LEGACY_POLL_INTERVAL_MS);

      const handleMoveEnd = () => { fetchLegacyData(); };
      map.on('moveend', handleMoveEnd);

      return () => {
        clearInterval(pollInterval);
        map.off('moveend', handleMoveEnd);
      };
    }
  }, [enabled, fetchCachedData, fetchLegacyData, map]);

  // ============================================================================
  // Rendering
  // ============================================================================

  /**
   * Returns a cached L.DivIcon for the (variant, heading-bucket) tuple.
   * Heading is rounded to the nearest 5° so most ticks reuse the previous
   * icon — see iconCacheRef comment above for the maths. Click registration
   * stayed-working pattern preserved from the pre-cache implementation:
   * single div with iconSize matching the visible SVG, no wrapper divs.
   */
  const getAircraftIcon = useCallback(
    (
      heading: number | null,
      isHelicopter: boolean,
      onGround: boolean,
      isEmergency?: boolean
    ): L.DivIcon => {
      const bucket = heading != null ? Math.round(heading / 5) * 5 : 0;
      const variant = `${isHelicopter ? 'h' : 'p'}|${isEmergency ? 'e' : 'n'}|${onGround ? 'g' : 'a'}|${bucket}`;
      const cached = iconCacheRef.current.get(variant);
      if (cached) return cached;

      const rotation = bucket;
      // Emergency aircraft: red. Helicopters/normal: blue.
      const color = isEmergency ? '#dc2626' : '#2563eb';
      const opacity = onGround ? 0.5 : 1;

      const iconSvg = isHelicopter
        ? `<svg viewBox="0 0 32 32" width="32" height="32" style="transform: rotate(${rotation}deg); opacity: ${opacity};">
            <line x1="4" y1="12" x2="28" y2="12" stroke="${color}" stroke-width="2.5" stroke-linecap="round"/>
            <ellipse cx="16" cy="14" rx="5" ry="8" fill="${color}"/>
            <ellipse cx="16" cy="7" rx="3" ry="4" fill="${color}"/>
            <rect x="14" y="20" width="4" height="8" fill="${color}"/>
            <line x1="10" y1="27" x2="22" y2="27" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
          </svg>`
        : `<svg viewBox="0 0 24 24" width="28" height="28" style="transform: rotate(${rotation}deg); opacity: ${opacity};">
            <path fill="${color}" d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z"/>
          </svg>`;

      const size = isHelicopter ? 32 : 28;
      const icon = L.divIcon({
        className: 'aircraft-marker',
        html: `<div style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));">${iconSvg}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
      });
      iconCacheRef.current.set(variant, icon);
      return icon;
    },
    []
  );

  if (!enabled) {
    return null;
  }

  // Helper to get display lat/lon (interpolated or raw)
  const getDisplayPosition = (ac: InterpolatedAircraft | AircraftWithHistory): [number, number] | null => {
    if ('interpLat' in ac) {
      return [ac.interpLat, ac.interpLon];
    }
    if (ac.latitude !== null && ac.longitude !== null) {
      return [ac.latitude, ac.longitude];
    }
    return null;
  };

  const getDisplayHeading = (ac: InterpolatedAircraft | AircraftWithHistory): number | null => {
    if ('interpHeading' in ac) {
      return ac.interpHeading;
    }
    return ac.heading;
  };

  // Trail positions are pre-built in trailsByIcaoRef on each server poll;
  // see fetchCachedData / fetchLegacyData. This function is a lookup-only
  // accessor so the JSX iteration doesn't allocate per tick.
  const getTrailPositions = (ac: InterpolatedAircraft | AircraftWithHistory): [number, number][] | undefined => {
    return trailsByIcaoRef.current.get(ac.icao24);
  };

  // Viewport filter — only render aircraft inside the current bounds + a
  // 20% buffer so markers entering the viewport don't pop in at the edge.
  // Off-screen aircraft were burning CPU on icon lookups and trail builds
  // for no visual gain.
  const isInViewport = (lat: number, lon: number): boolean => {
    if (!viewBounds) return true; // pre-init: don't filter
    const latPad = (viewBounds.north - viewBounds.south) * 0.2;
    const lonPad = (viewBounds.east - viewBounds.west) * 0.2;
    return (
      lat >= viewBounds.south - latPad &&
      lat <= viewBounds.north + latPad &&
      lon >= viewBounds.west - lonPad &&
      lon <= viewBounds.east + lonPad
    );
  };

  return (
    <>
      {/* Aircraft trail polylines */}
      {aircraft.map((ac) => {
        const pos = getDisplayPosition(ac);
        if (!pos || !isInViewport(pos[0], pos[1])) return null;
        const positions = getTrailPositions(ac);
        if (!positions || positions.length < 2) return null;
        const isEmergency = 'is_emergency' in ac && ac.is_emergency;
        const color = isEmergency ? '#dc2626' : '#2563eb';

        return (
          <Polyline
            key={`trail-${ac.icao24}`}
            positions={positions}
            pathOptions={{
              color,
              weight: 2,
              opacity: 0.5,
              dashArray: '5, 5',
            }}
          />
        );
      })}

      {/* Aircraft markers */}
      {aircraft.map((ac) => {
        const pos = getDisplayPosition(ac);
        if (!pos || !isInViewport(pos[0], pos[1])) return null;

        const heading = getDisplayHeading(ac);
        const isEmergency = ac.is_emergency ?? false;
        const isStale = ('isStale' in ac) ? ac.isStale : false;

        // Capture icao24 for the ref callback closure. The interpolation
        // tick uses this ref map to setLatLng/setIcon imperatively.
        const icao = ac.icao24;
        return (
          <Marker
            key={icao}
            ref={(m) => {
              if (m) {
                markerRefsByIcaoRef.current.set(icao, m);
              } else {
                markerRefsByIcaoRef.current.delete(icao);
              }
            }}
            position={pos}
            icon={getAircraftIcon(heading, ac.is_helicopter, ac.on_ground, isEmergency)}
            opacity={isStale ? 0.4 : 1}
          >
            <Popup maxWidth={520} minWidth={320} className="aircraft-popup-wrapper">
              <AircraftDetailCard
                aircraft={ac}
                showFollowButton={!!onRideAlong}
                onRideAlong={onRideAlong}
              />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
